import * as React from "react";
import { Dialog, DialogTitle, DialogContent, DialogActions, Button, 
  Snackbar, Alert, TextField, Typography, Paper, FormControl, Box,
  InputLabel, MenuItem, Chip, Backdrop, CircularProgress, IconButton,
  TableContainer, Table, TableHead, TableRow, TableBody, Checkbox,
  Autocomplete} from "@mui/material";

import ControlPointIcon from '@mui/icons-material/ControlPoint';
import TableCell, { tableCellClasses } from '@mui/material/TableCell';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';

import { Stack } from "@mui/system";
import ListItemText from '@mui/material/ListItemText';
import Select, { SelectChangeEvent } from '@mui/material/Select';

import * as ATCAConstants from '../util/ATCAConstants'
import * as Utils from '../util/Utils'
import { post } from 'aws-amplify/api';

import { ProjectContext } from '../schedule/ProjectContext';
import { styled } from '@mui/material/styles';


import 'dayjs/locale/en-gb';
import dayjs, { Dayjs} from 'dayjs';
import utc from 'dayjs/plugin/utc';

dayjs.extend(utc);

const StyledTableCell = styled(TableCell)(({ theme }) => ({
  [`&.${tableCellClasses.body}`]: {
    borderBottom: 'none',
    padding: '5px 10px 0 10px'
  },
}));

const StyledTextTableCell = styled(TableCell)(({ theme }) => ({
  [`&.${tableCellClasses.body}`]: {
    borderBottomColor: '#eeeeee',
    padding: '10px 0 10px 0'
  },
}));

const StyledTableRow = styled(TableRow)(({ theme }) => ({
  '&:nth-of-type(even)': {
    backgroundColor: theme.palette.action.hover,
  },
  // hide last border
  '&:last-child td, &:last-child th': {
    border: 0,
  },
}));

const ITEM_HEIGHT = 35;
const ITEM_PADDING_TOP = 8;
const MenuProps = {
  PaperProps: {
    style: {
      maxHeight: ITEM_HEIGHT * 12 + ITEM_PADDING_TOP,
      // width: 250,
    },
  },
};

export default function VeloDialog(
  props: {
    open: boolean,
    utcDate: string,
    handleClose: () => void,
    corrConfig: any,
    updateZoomBands: (zoomBands: any[]) => void,
    updateZoomConfiguration: (targetNames: string[], restFreqs: Number[]) => void
  }
) {
  const [snackInfo, setSnackInfo] = React.useState<any>({
    open: false,
    message: '',
    severity: 'info'
  });

  const projectContext = React.useContext(ProjectContext);
  const [selectedTargets, setSelectedTargets] = React.useState<any[]>([]);
  const [projectTargets, setProjectTargets] = React.useState<any[]>([]);
  const [restFrequencies, setRestFrequencies] = React.useState<string[]>([]);
  const [recommendedBands, setRecommendedBands] = React.useState<any[]>([]);
  const [disableCalculate, setDisableCalculate] = React.useState(true);
  const [calculatedZoomValues, setCalculatedZoomValues] = React.useState<any>({});
  const [targetMatrix, setTargetMatrix] = React.useState<any[]>([]);
  const [openBackdrop, setOpenBackdrop] = React.useState(false);

  React.useEffect(() => {
    if (props.open) {
      const targetList = projectContext.getTargets();
      setProjectTargets(JSON.parse(JSON.stringify(targetList)));

      const zooms = props.corrConfig['sub_band_configuration'] || [];
      const bands = JSON.parse(JSON.stringify(zooms));
      setRecommendedBands(bands);
      calculateZoomValues(bands);

      const targetNames = props.corrConfig['zoom_targets'] || [];
      const list = targetList.filter(t => targetNames.includes(t['name']));
      setSelectedTargets(JSON.parse(JSON.stringify(list)));

      const freqs = props.corrConfig['zoom_rest_frequencies'] || [];
      setRestFrequencies(freqs.map((f: any) => '' + f));

      setTargetMatrix([]);
    }
  }, [props.open]);


  React.useEffect(() => {
    if ( (selectedTargets.length > 0) && (restFrequencies.length>0)) {
      setDisableCalculate(false);
    } else {
      setDisableCalculate(true);
    }
  }, [selectedTargets, restFrequencies]);

  const addZoomBand = () => {
    const band =  {
      id: Date.now(),
      band: '',
      subband: '',
      centre_frequency: '',
      zoom: '',
    };

    const bands = [...recommendedBands];
    bands.push(band);
    setRecommendedBands(bands);
  }

  const deleteBand = (band: any) => {
    const newBands = recommendedBands.filter((b: any) => b.id !== band.id);
    setRecommendedBands(newBands);
  }

  const isTargetWithinZoom = (target: any) => {
    const skyFrequency = target['sky_frequency'];

    for (const recommendedBand of recommendedBands) {
      const zoom = getZoom(recommendedBand['zoom']);

      if (zoom && skyFrequency > 0) {
        const bandWidth = zoom['points'] * zoom['spectral_resolution'] / 1000;
        const centreFreq = Number(recommendedBand['centre_frequency']);

        if (bandWidth > 0 && centreFreq > 0) {
          const highFreq = centreFreq + bandWidth /2;
          const lowFreq = centreFreq - bandWidth / 2;

          if (skyFrequency > highFreq || skyFrequency < lowFreq ) {
            continue;
          } else {
            return [lowFreq, highFreq];
          }
        }
      }
    }

    return null;
  }

  const suggestBand = (centreFrequency: number) => {

    const settings = projectContext.correlatorSettings.filter(
      (setting: any) => setting['name'] === props.corrConfig['correlator_setting']);

    const band = Utils.caluculateBand(centreFrequency, 
      props.corrConfig['frequency_configuration']||{}, settings[0]);

    if (band && !band['messages']) {
      return {
        id: Date.now(),
        band: band['band'],
        subband: band['subband'],
        centre_frequency: centreFrequency.toFixed(3),
        zoom: ''
      };
    }

    return band;
  }

  const calculateZoomValues = (zoomBands: any[]) => {
    const calculatedValues: any[] = [];

    for (const zoomBand of zoomBands) {
      let calculatedValue: any = {};

      let centerFreq = 0;
      if (zoomBand && zoomBand['centre_frequency']) {
        centerFreq = Number(zoomBand['centre_frequency']);
      }
      if (!zoomBand['zoom'] || centerFreq <= 0) {
        calculatedValue[zoomBand['id']] = {};
        continue;
      }

      const zoomName = zoomBand['zoom'];
      const zoom = Utils.getZoomBand(
        projectContext.correlatorSettings,
        props.corrConfig['correlator_setting'], zoomName);
    
      const velocityResolution = ATCAConstants.SPEED_OF_LIGHT * zoom['spectral_resolution']
        / (1000 * centerFreq);

      let x = Math.floor(velocityResolution);
      let d = velocityResolution - x;
      const vResolution = x + Number(d.toPrecision(3));

      const velocityWidth = velocityResolution  * zoom['points'];
      x = Math.floor(velocityWidth);
      d = velocityWidth - x;
      const vWidth = x + Number(d.toPrecision(3));
      
      calculatedValue = {
        'velocityWidth': vWidth,
        'velocityResolution': vResolution.toFixed(3),
        'dataRate': zoom['data_rate'],
        'points': zoom['points'],
        'spectralResolution': zoom['spectral_resolution'],
        'unit': zoom['unit'],
      };
      calculatedValues[zoomBand['id']] = calculatedValue;
    }

    setCalculatedZoomValues(calculatedValues);
    fetchTargetVelocities(zoomBands, targetMatrix);
  }

  const fetchTargetVelocities = (zoomBands: any[], theTargetMatrix: any) => {
    const targetList = [];

    for (const band of zoomBands) {
      const zoom = getZoom(band['zoom']);

      if (!theTargetMatrix || theTargetMatrix.length === 0)
        return;

      if (!zoom || !band || band['centre_frequency'] <= 0)
        continue;

      for (const target of theTargetMatrix) {
        const freq = isTargetWithinZoom(target);
        if (!freq) {
          continue;
        }

        const data = JSON.parse(JSON.stringify(target));
        data["utc_date"] = (props.utcDate||'').substring(0, 10);
        data["sky_frequencies"] = freq;
        targetList.push(data);
      }
    }

    if (targetList.length <= 0) {
      setTargetMatrix(theTargetMatrix);
      return;
    }

    const postOperation = post(
      {
        apiName: 'cabb',
        path: '/velo_velocities',
        options: {
          body: targetList,
          headers: {'content-type': 'application/json'}
        }
      }
    );

    postOperation.response
      .then(response => response.body.json())
      .then((response: any) => {
      if (response['status'] === 'success') {
          const results: any[] = response['result'];
          const matrix: any[] = JSON.parse(JSON.stringify(theTargetMatrix));
          for (const target of matrix) {
            const result: any[] = results.filter(t => 
              (t['target_name'] === target['name']) && (t['rest_frequency'] === target['rest_frequency'])
            );
            if (result.length > 0 && result[0]['velocities'] 
              && result[0]['velocities'].length === 2) {

              const velocities = result[0]['velocities'];
              target['zoom_vel_low'] = velocities[0].toFixed(3);
              target['zoom_vel_high'] = velocities[1].toFixed(3);
            }
          }
          setTargetMatrix(matrix);
      } else {
        const message = response['message'];
        if (message)
          setSnackInfo({
            open: true,
            message: message,
            severity: 'error'
          });
      }
    })
    .catch((err: Error) => {
      setSnackInfo({
        open: true,
        message: 'Could not caculate sky frequency: ' + err.message,
        severity: 'error'
      });
    });

  }

  const saveUpdates = () => {
    props.updateZoomBands(recommendedBands);
  }

  const setZoom = (band: any, event: SelectChangeEvent) => {
    band['zoom'] = event.target.value as string;
    const bands = [...recommendedBands];
    setRecommendedBands(bands);
    calculateZoomValues(bands);
  }

  const setCentreFreq = (band: any,
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {

    // change band/subband if neccessary
    const zoomBand: any = suggestBand(Number(event.target.value));
    band['centre_frequency'] = event.target.value;
    if (zoomBand && !zoomBand['messages']) {
      band['band'] = zoomBand['band'];
      band['subband'] = zoomBand['subband'];
    } else if (zoomBand){
      band['message'] = zoomBand['messages']
    }

    const bands = [...recommendedBands];
    setRecommendedBands(bands);
    calculateZoomValues(bands);
  }

  const changeSelectedTargets = (event: SelectChangeEvent<any[]>) => {
    const targetNames = event.target.value as any[];
    const targets = projectTargets.filter(t => targetNames.includes(t['name']));
    setSelectedTargets(targets);
    const freqs = restFrequencies.map(t => Number(t));
    props.updateZoomConfiguration([...targetNames], freqs);
  }

  const handleCalculate = () => {
    setOpenBackdrop(true);

    setTargetMatrix([]);

    const targets = [];

    for (const target of selectedTargets ) {
      for (const rest_frequency of restFrequencies) {
        const data = {
          "name": target['name'],
          "ra": target['ra'],
          "dec": target['dec'],
          "coord_sys": target['coord_sys']||'J2000',
          "utc_date": (props.utcDate||'').substring(0, 10),
          "velocity": Utils.getTargetVelocityString(target),
          "rest_frequency": Number(rest_frequency)
        };
        targets.push(data);
      }
    }
    
    const postOperation = post(
      {
        apiName: 'cabb',
        path: '/velo',
        options: {
          body: targets,
          headers: {'content-type': 'application/json'}
        }
      }
    );

    postOperation.response
      .then(response => response.body.json())
      .then((response: any) => {
        if (response['status'] === 'success') {
          const results: any[] = response['result'];
          const matrix: any[] = [];
          const frequencies: number[] = [];
          const sortedResult = results.sort((a, b) => a['sky_frequency'] - b['sky_frequency'])
          for (const result of sortedResult) {
            const target = selectedTargets.filter((t) => t['name'] === result['target_name']);
            const calculatedTarget = JSON.parse(JSON.stringify(target[0]));
            calculatedTarget['sky_frequency'] = result['sky_frequency'];
            calculatedTarget['rest_frequency'] = result['rest_frequency'];
            calculatedTarget['zoom_vel_low'] = 0;
            calculatedTarget['zoom_vel_high'] = 0;

            matrix.push(calculatedTarget);
            frequencies.push(result['sky_frequency']);
          }
          fetchTargetVelocities(recommendedBands, matrix);
        } else {
          const message = response['message'];
          if (message)
            setSnackInfo({
              open: true,
              message: message,
              severity: 'error'
            });
        }
      })
      .catch((err: Error) => {
        setSnackInfo({
          open: true,
          message: 'Could not calculate sky frequency: ' + err.message,
          severity: 'error'
        });
      })
      .finally(() => {
        setOpenBackdrop(false);
      });
  }

  const containsTarget = (target: any) => {
    const targets = (selectedTargets || []).filter(t => t['name'] === target['name']);

    return targets.length > 0;
  }

  const renderZoomValue = (value: string) => {
    const zoom = getZoom(value);
    if (zoom) {
      return (
        <Stack direction="row" spacing={2} alignItems={'center'}>
          <Typography variant="body1">
            {zoom['name'] || ''}
          </Typography>
          <Typography variant="body2">
            {(zoom['points'] * zoom['spectral_resolution']).toFixed(3)} kHz
          </Typography>
          <Typography variant="body2">
            {zoom['data_rate']} kB/s
          </Typography>
        </Stack>
      );
    } else {
      return (
        <Typography variant="body1"></Typography>
      );    
    }
  }

  const renderTargetValues = (value: any[]) => {
    return (
      <Box sx={{ display: 'flex', overflowY: 'scroll',
      flexWrap: 'wrap', gap: 0.5, maxHeight: '70px' }}>
        {
          value.map((t: any) => (
            <Chip key={t} label={t} />
            )
          )
        }
      </Box>
    );
  };

  const getSubBandInfo = (recommendedBand: any) => {
    const freqSetting = props.corrConfig['frequency_configuration'];
    if (freqSetting) {
      const band = recommendedBand['band'];
      const subband = recommendedBand['subband'];
      const startFreq = Utils.getStartFreq(freqSetting, band, subband);
      const endFreq = Utils.getEndFreq(freqSetting, band, subband);
      const freqRange =  ((startFreq > 0) ? startFreq : '') + ' - ' + ((endFreq > 0) ? endFreq : '');

      return freqRange;
    }

    return '';
  }

  const getZooms = () => {
    return Utils.getZoomConfigurations(
      projectContext.correlatorSettings,
      props.corrConfig['correlator_setting']);
  }

  const getZoom = (name: string) => {
    const zooms: any[] = Utils.getZoomConfigurations(
      projectContext.correlatorSettings,
      props.corrConfig['correlator_setting']);
    if (zooms && zooms.length>0) {
      const results = zooms.filter(z => z['name'] === name);
      if (results && results.length>0)
        return results[0];
    }

    return null;
  }

  const getStatusRow = (status: any) => (
    (status['status'] !== 'valid') &&
    <TableRow>
      <TableCell align="center" colSpan={8} 
        sx={{ height: '25px', padding: 0, color: status['color'] }}>
        {status['status'] === 'valid' ? '' : status['status'] + ': ' + status['message']}
      </TableCell>
    </TableRow>
  )

  const targetValueTable = (
    <TableContainer component={Paper} sx={{width: '100%'}}>
      <Table aria-label="simple table">
        <TableHead sx={{ backgroundColor: '#cfd8dc'}}>
          <TableRow>
            <TableCell width={'20%'} align="center">Target Name</TableCell>
            <TableCell width={'20%'} align="center">Rest Frequency</TableCell>
            <TableCell width={'20%'} align="center">Sky Frequency (MHz)</TableCell>
            <TableCell width={'20%'} align="center">Zoom Vel low (km/s)</TableCell>
            <TableCell width={'20%'} align="center">Zoom Vel high (km/s)</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {targetMatrix.map((target) => (
            <StyledTableRow key={target['name']}
              sx={{ backgroundColor: isTargetWithinZoom(target) ? 'inherits' : '#ffcdd2 !important'}}>
              <StyledTextTableCell align="center">
                {target['name']}
              </StyledTextTableCell>
              <StyledTextTableCell align="center">
                {target['rest_frequency']}
              </StyledTextTableCell>
              <StyledTextTableCell align="center">
                {target['sky_frequency']}
              </StyledTextTableCell>
              <StyledTextTableCell align="center">
                {target['zoom_vel_low']}
              </StyledTextTableCell>
              <StyledTextTableCell align="center">
                {target['zoom_vel_high']}
              </StyledTextTableCell>
            </StyledTableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );

  const subBandTable = (
    <React.Fragment>
      <TableContainer component={Paper} sx={{
        width: '100%'
      }}>
        <Table aria-label="simple table">
          <TableHead sx={{ backgroundColor: '#cfd8dc', padding: '0' }}>
            <TableRow>
              <TableCell padding={'none'} width={'5%'} align="center">Band</TableCell>
              <TableCell padding={'none'} width={'5%'} align="center">Sub Band</TableCell>
              <TableCell padding={'none'} width={'13%'} align="center">Center Frequency (MHz)</TableCell>
              <TableCell padding={'none'} width={'30%'} sx={{ minWidth: '175px' }} align="center">Zoom Config</TableCell>
              <TableCell padding={'none'} width={'12%'} align="center">Frequency Range (MHz)</TableCell>
              <TableCell padding={'none'} width={'10%'} align="center">Velocity Width (km/s)</TableCell>
              <TableCell padding={'none'} width={'10%'} align="center">Velocity Resolution</TableCell>
              <TableCell padding={'none'} width={'5%'} align="center"></TableCell>
            </TableRow>
          </TableHead>
          {
            <TableBody>
            {recommendedBands.map((recommendedBand: any) => (
              <>
                <TableRow>
                  <StyledTableCell align="center">
                    {recommendedBand['band']}
                  </StyledTableCell>
                  <StyledTableCell align="center">
                    {recommendedBand['subband']}
                  </StyledTableCell>

                  <StyledTableCell align="center">
                    <TextField id={`center-freq-{index}`} label="Centre Frequency"
                      variant="standard" sx={{ flexGrow: 1 }}
                      value={recommendedBand.centre_frequency || ''}
                      fullWidth
                      onChange={e => setCentreFreq(recommendedBand, e)}
                      inputProps={{ style: { textAlign: 'right' } }}
                    />
                  </StyledTableCell>

                  <StyledTableCell align="center">
                    <FormControl 
                      aria-label="Select zoom band"
                      fullWidth variant="standard">
                      <InputLabel id="zoom-selection">Zoom band</InputLabel>
                      <Select
                        labelId="zoom-selection-label"
                        id="zoom-selection-value"
                        label="Zoom band"
                        displayEmpty={true}
                        renderValue={renderZoomValue}
                        value={recommendedBand['zoom'] || ''}
                        onChange={e => setZoom(recommendedBand, e)}
                      >
                        {getZooms().map((config: any) => (
                          <MenuItem key={config['name']} value={config['name']}
                            disabled={
                              Utils.zoomExists(
                                recommendedBands,
                                recommendedBand['band'], 
                                recommendedBand['subband'], 
                                config['name'])}
                          >
                            <ListItemText primary={config['name']}
                              secondary={
                                <Stack direction="row" spacing={2}>
                                  <Typography variant="body2">
                                    {config['points']} points
                                  </Typography>
                                  <Typography variant="body2">
                                    {config['spectral_resolution'].toFixed(3)} {config['unit']}
                                  </Typography>
                                  <Typography variant="body2">
                                    {(config['points'] * config['spectral_resolution']).toFixed(3)} kHz
                                  </Typography>
                                  <Typography variant="body2">
                                    {config['data_rate']} kB/s
                                  </Typography>
                                </Stack>
                              }
                            />
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </StyledTableCell>

                  <StyledTableCell align="center">
                    {getSubBandInfo(recommendedBand)}
                  </StyledTableCell>

                  <StyledTableCell align="center">
                      {(calculatedZoomValues[recommendedBand['id']]||{})['velocityWidth']}
                  </StyledTableCell>
                  <StyledTableCell align="center">
                      {(calculatedZoomValues[recommendedBand['id']]||{})['velocityResolution']}
                  </StyledTableCell>
                  <StyledTableCell align="center">
                    <IconButton
                      sx={{"&:hover": { color: '#c62828' } }}
                      onClick={e => deleteBand(recommendedBand)} 
                      edge="end" aria-label="delete">
                      <DeleteOutlineIcon />
                    </IconButton>
                  </StyledTableCell>
                </TableRow>
                {
                  getStatusRow(Utils.validateZoomBand(
                    projectContext.correlatorSettings,
                    props.corrConfig,
                    recommendedBand))
                }
                <TableRow>
                  <TableCell colSpan={8} align="center">
                      {(recommendedBand['messages']||[]).map((message: string, index: number) => (
                        <Typography key={'message_' + index} sx={{ color: '#ef5350'}}>
                        {message}
                      </Typography>
                    ))}
                  </TableCell>
                </TableRow>
              </>
            ))}
            </TableBody>
          }
        </Table>
      </TableContainer>
    </React.Fragment>
  );

  const changeRestFrequencies = (newValue: any[]) => {
    const freqList: string[] = [];
    for (const freq of newValue) {
      if (typeof(freq) === 'string') {
        freqList.push(freq.toString());
      } else {
        freqList.push(freq['freq'].toString());
      }
    }
    setRestFrequencies(freqList);

    const targetNames = selectedTargets.map(t => t['name']);
    const freqs = freqList.map(t => Number(t));
    props.updateZoomConfiguration(targetNames, freqs);
  }

  const checkOption = (option: any, val: any) => {
    return option['freq'] === val;
  }

  const getDateColor = () => {
    if (props.utcDate) {
      const date = dayjs.utc((props.utcDate||'').substring(0, 10));
      const today = dayjs.utc().hour(0).minute(0).second(0).millisecond(0);

      if (date.isBefore(today)) {
        return 'red'
      }
    }

    return '';
  }

  return (
    <Dialog open={props.open}
      aria-labelledby="frequency-calculator-dialog-title"
      PaperProps={{ sx: {height: '100%', overflowY: 'auto'} }}
      onClose={props.handleClose}
      maxWidth="lg" fullWidth>
      <Backdrop
        sx={
          (theme) => ({ color: '#fff', zIndex: theme.zIndex.drawer + 1 })
        }
        open={openBackdrop}
        onClick={e => setOpenBackdrop(false)}
      >
        <CircularProgress color="inherit" />
      </Backdrop>

      <DialogTitle id="frequency-calculator-dialog-title">
        <Stack direction={'row'} justifyContent={'space-between'} alignItems={'center'}>
          <Typography variant="h6">Frequency calculator</Typography>
          <Typography color={getDateColor()}>{(props.utcDate||'').substring(0, 10)}</Typography>
        </Stack>
      </DialogTitle>
      <DialogContent sx={{height: '100%'}}>
        <Paper sx={{ height: '100%', width: '100%'}}>
          <Stack spacing={2} padding={'0 20px 20px 20px'} height={'calc(100% - 20px)'}>
            <Stack direction={'row'} spacing={2} height={'100px'}
              alignItems={'flex-end'}>

              <FormControl variant="standard" sx={{ width: '50%' }}>
                <InputLabel id="targets-label">Targets</InputLabel>
                <Select
                  labelId="targets-label"
                  id="selected_targets"
                  label="Targets"
                  displayEmpty={true}
                  multiple
                  value={selectedTargets.map(t => t['name'])}
                  onChange={changeSelectedTargets}
                  renderValue={renderTargetValues}
                  MenuProps={MenuProps}
                >
                  {projectTargets.map((target, index) => (
                    <MenuItem key={target['name'] + ' ' + index} value={target['name']}>
                      <Checkbox checked={containsTarget(target)} />
                      <ListItemText primary={target['name']}
                        secondary={
                          <Typography variant="body2">
                            {target['ra']} {target['dec']} {target['coord_sys']||'J2000'} {Utils.getTargetVelocityString(target)} 
                          </Typography>
                        }
                      />
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <Autocomplete sx={{ width: '50%' }}
                multiple
                id="rest-frequencies"
                options={ATCAConstants.SPECTRAL_LINE_FREQUENCIES}
                getOptionLabel={(option: any) => option}
                isOptionEqualToValue={(option, val) => checkOption(option, val)}
                freeSolo
                value={restFrequencies || []}
                onChange={(event, newValue) => changeRestFrequencies(newValue)}
                renderOption={(props, option, { selected }) => {
                  const { ...optionProps } = props;
                  return (
                    <Box
                      component="li"
                      sx={{backgroundColor: selected ? 'rgba(0, 75, 135, 0.08)' : ''}}
                      {...optionProps}
                      key={option['freq']}
                    >
                      <ListItemText primary={option['name']}
                        secondary={
                          <Typography variant="body2">
                            {option['freq']} MHz
                          </Typography>
                        }
                      />
                    </Box>
                )}}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    variant="standard"
                    label="Rest Frequency"
                    placeholder="MHz"
                  />
                )}
              />

              {/* <Typography sx={{flexGrow: 4}}>
                {props.utcDate instanceof Date ? props.utcDate.toISOString() : props.utcDate}
              </Typography> */}
            </Stack>

            <Stack spacing={2} direction={'row'} justifyContent={'space-between'}>
              <Button disabled={disableCalculate}
                sx={{flexGrow: 4}}
                variant="outlined" size="small"
                onClick={handleCalculate}>
                Calculate
              </Button>
              <Button variant="text"
                onClick={addZoomBand}
                startIcon={<ControlPointIcon />}>
                Add Zoom Band
              </Button>
            </Stack>

            <Stack sx={{flexGrow: 4, maxHeight: '300px'}}>
              {targetValueTable}
            </Stack>

            <Stack sx={{flexGrow: 4, maxHeight: '300px'}}>
              {subBandTable}
            </Stack>

          </Stack>
        </Paper>
      </DialogContent>

      <DialogActions>
        <Button onClick={saveUpdates}>
          Update
        </Button>
        <Button onClick={props.handleClose}>
          Close
        </Button>
      </DialogActions>

      <Snackbar
        role = "alert"
        aria-label = {"Alert message"}
        open={snackInfo['open']}
        autoHideDuration={6000}
        onClose={e => setSnackInfo({
          open: false,
          message: '',
          severity: 'info'
        })}
      >
        <Alert severity={snackInfo['severity']}>
          {snackInfo['message']}
        </Alert>
      </Snackbar>
    </Dialog>
  );
};
